import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, FileText, Package, Award, Briefcase,
  ChevronDown, ChevronUp, Lock, Crown, Save, Plus, Trash2,
  Info, MapPin, Phone, Mail, Instagram, Globe, Users, TrendingUp, Loader
} from 'lucide-react';
import ProviderReferences from './ProviderReferences';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProviderProfileEdit = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    description: true,
    offers: false,
    trust: false,
    b2b: false
  });

  // Form data
  const [formData, setFormData] = useState({
    // Basic fields
    company_name: '',
    contact_person: '',
    categories: [],
    location_city: '',
    location_zip: '',
    location_state: '',
    service_radius_km: 50,
    phone: '',
    mobile: '',
    email: '',
    instagram_link: '',
    website_link: '',
    
    // Description & Media
    short_description: '',
    long_description: '',
    base_price_from: '',
    base_price_numeric: null,
    years_of_experience: null,
    
    // Plus fields
    specializations: [],
    packages: [],
    service_areas_detail: '',
    languages: [],
    availability_hint: '',
    top_references: [],
    certifications: [],
    
    // Pro fields
    b2b_focus: false,
    b2b_event_types: [],
    b2b_description: '',
    capacity_range: '',
    travel_scope: '',
    company_legal_form: '',
    team_size_range: '',
    has_liability_insurance: false,
    has_vat_id: false,
    b2b_invoice_hint: '',
    reference_clients: [],
    case_studies: [],
    team_members: []
  });

  const planLevel = profile?.plan_level || 'basic';
  const hasActiveSubscription = profile?.subscription_status === 'active' || profile?.subscription_status === 'trial';

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/provider/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile(response.data);
      setFormData({
        company_name: response.data.company_name || '',
        contact_person: response.data.contact_person || '',
        categories: response.data.categories || [],
        location_city: response.data.location_city || '',
        location_zip: response.data.location_zip || '',
        location_state: response.data.location_state || '',
        service_radius_km: response.data.service_radius_km || 50,
        phone: response.data.phone || '',
        mobile: response.data.mobile || '',
        email: response.data.email || '',
        instagram_link: response.data.instagram_link || '',
        website_link: response.data.website_link || '',
        short_description: response.data.short_description || '',
        long_description: response.data.long_description || '',
        base_price_from: response.data.base_price_from || '',
        base_price_numeric: response.data.base_price_numeric || null,
        years_of_experience: response.data.years_of_experience || null,
        specializations: response.data.specializations || [],
        packages: response.data.packages || [],
        service_areas_detail: response.data.service_areas_detail || '',
        languages: response.data.languages || [],
        availability_hint: response.data.availability_hint || '',
        top_references: response.data.top_references || [],
        certifications: response.data.certifications || [],
        b2b_focus: response.data.b2b_focus || false,
        b2b_event_types: response.data.b2b_event_types || [],
        b2b_description: response.data.b2b_description || '',
        capacity_range: response.data.capacity_range || '',
        travel_scope: response.data.travel_scope || '',
        company_legal_form: response.data.company_legal_form || '',
        team_size_range: response.data.team_size_range || '',
        has_liability_insurance: response.data.has_liability_insurance || false,
        has_vat_id: response.data.has_vat_id || false,
        b2b_invoice_hint: response.data.b2b_invoice_hint || '',
        reference_clients: response.data.reference_clients || [],
        case_studies: response.data.case_studies || [],
        team_members: response.data.team_members || []
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Fehler beim Laden des Profils');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/provider/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profil gespeichert');
      loadProfile(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const canAccessPlus = () => planLevel === 'plus' || planLevel === 'pro';
  const canAccessPro = () => planLevel === 'pro';

  const addToArray = (field, value) => {
    if (!value.trim()) return;
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], value.trim()]
    }));
  };

  const removeFromArray = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const addPackage = () => {
    setFormData(prev => ({
      ...prev,
      packages: [...prev.packages, { 
        name: '', 
        description: '', 
        price_from: '', 
        features: [] 
      }]
    }));
  };

  const updatePackage = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      packages: prev.packages.map((pkg, i) => 
        i === index ? { ...pkg, [field]: value } : pkg
      )
    }));
  };

  const removePackage = (index) => {
    setFormData(prev => ({
      ...prev,
      packages: prev.packages.filter((_, i) => i !== index)
    }));
  };

  const UpgradeBadge = ({ plan }) => (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-full">
      <Crown className="w-4 h-4" />
      Im {plan}-Abo verfügbar
    </div>
  );

  const SectionHeader = ({ icon: Icon, title, expanded, onToggle, locked, plan }) => (
    <div 
      onClick={onToggle}
      className="flex items-center justify-between p-4 bg-gradient-to-r from-[#FFF0D1]/30 to-[#F4F2F2] rounded-xl cursor-pointer hover:from-[#FFF0D1] hover:to-[#F4F2F2] transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon className="w-6 h-6 text-[#2B2142]" />
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        {locked && <UpgradeBadge plan={plan} />}
      </div>
      {expanded ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
    </div>
  );

  const LockedOverlay = ({ plan }) => (
    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
      <div className="text-center p-6">
        <Lock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-lg font-semibold text-gray-900 mb-2">
          Im {plan}-Abo verfügbar
        </p>
        <p className="text-sm text-gray-600 mb-4">
          Upgrade für erweiterte Profilfelder
        </p>
        <a
          href="/pricing"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2B2142] to-[#3D3158] text-white font-semibold rounded-xl hover:from-[#1E1730] hover:to-[#2B2142] transition-all"
        >
          <Crown className="w-5 h-5" />
          Jetzt upgraden
        </a>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-[#2B2142]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plan Info Banner */}
      {hasActiveSubscription ? (
        <div className={`p-4 rounded-xl ${
          planLevel === 'pro' ? 'bg-gradient-to-r from-purple-100 to-pink-100' :
          planLevel === 'plus' ? 'bg-gradient-to-r from-[#FFF0D1] to-[#F4F2F2]' :
          'bg-gray-100'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Aktueller Plan</p>
              <p className="text-2xl font-bold capitalize">
                {planLevel === 'pro' && '🏆 Pro'}
                {planLevel === 'plus' && '⭐ Plus'}
                {planLevel === 'basic' && '📦 Basic'}
              </p>
            </div>
            {planLevel !== 'pro' && (
              <a
                href="/pricing"
                className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-xl shadow-sm transition-colors"
              >
                Plan upgraden
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Aktueller Plan</p>
              <p className="text-2xl font-bold text-gray-700">Noch kein Plan aktiv</p>
            </div>
            <a
              href="/pricing"
              className="px-6 py-3 bg-gradient-to-r from-[#2B2142] to-[#3D3158] hover:from-[#1E1730] hover:to-[#2B2142] text-white font-semibold rounded-xl shadow-sm transition-all"
            >
              Plan wählen
            </a>
          </div>
        </div>
      )}

      {/* Visibility Warning Banner */}
      {profile?.missing_required_fields && profile.missing_required_fields.length > 0 && (
        <div className="p-4 rounded-xl bg-orange-50 border-2 border-orange-200">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <Info className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-orange-800 mb-1">Dein Profil ist nicht sichtbar</h3>
              <p className="text-sm text-orange-700 mb-2">
                Um in der Suche angezeigt zu werden, fülle bitte folgende Pflichtfelder aus:
              </p>
              <div className="flex flex-wrap gap-2">
                {profile.missing_required_fields.map((field, idx) => (
                  <span key={idx} className="px-2 py-1 bg-orange-100 text-orange-800 text-sm rounded-lg font-medium">
                    {field}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section 1: Basisinformationen (alle Pläne) */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <SectionHeader 
          icon={Building2}
          title="Basisinformationen"
          expanded={expandedSections.basic}
          onToggle={() => toggleSection('basic')}
        />
        
        {expandedSections.basic && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Firmenname / Name *</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  placeholder="z.B. Event Fotografie Müller"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Ansprechpartner *</label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:border-[#2B2142] focus:outline-none ${!formData.contact_person ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
                  placeholder="Max Müller"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Stadt *</label>
                <input
                  type="text"
                  value={formData.location_city}
                  onChange={(e) => setFormData({...formData, location_city: e.target.value})}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:border-[#2B2142] focus:outline-none ${!formData.location_city ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
                  placeholder="Berlin"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">PLZ *</label>
                <input
                  type="text"
                  value={formData.location_zip}
                  onChange={(e) => setFormData({...formData, location_zip: e.target.value})}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:border-[#2B2142] focus:outline-none ${!formData.location_zip ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
                  placeholder="10115"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Bundesland *</label>
                <input
                  type="text"
                  value={formData.location_state}
                  onChange={(e) => setFormData({...formData, location_state: e.target.value})}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:border-[#2B2142] focus:outline-none ${!formData.location_state ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
                  placeholder="Berlin"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Service-Radius (km) *
              </label>
              <input
                type="number"
                value={formData.service_radius_km}
                onChange={(e) => setFormData({...formData, service_radius_km: parseInt(e.target.value) || 0})}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:border-[#2B2142] focus:outline-none ${!formData.service_radius_km || formData.service_radius_km <= 0 ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
                min="1"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Telefon *</label>
                <input
                  type="tel"
                  inputMode="tel"
                  maxLength={20}
                  pattern="[+\d\s\-()]*"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/[^\d+\s\-()]/g, '')})}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:border-[#2B2142] focus:outline-none ${!formData.phone ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
                  placeholder="+49 30 12345678"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Mobil</label>
                <input
                  type="tel"
                  inputMode="tel"
                  maxLength={20}
                  pattern="[+\d\s\-()]*"
                  value={formData.mobile}
                  onChange={(e) => setFormData({...formData, mobile: e.target.value.replace(/[^\d+\s\-()]/g, '')})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  placeholder="+49 170 1234567"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">E-Mail (öffentlich) *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:border-[#2B2142] focus:outline-none ${!formData.email ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
                placeholder="kontakt@beispiel.de"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Instagram className="w-4 h-4 inline mr-2" />
                  Instagram
                </label>
                <input
                  type="url"
                  value={formData.instagram_link}
                  onChange={(e) => setFormData({...formData, instagram_link: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  placeholder="https://instagram.com/..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Globe className="w-4 h-4 inline mr-2" />
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website_link}
                  onChange={(e) => setFormData({...formData, website_link: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section 2: Beschreibung & Medien (alle Pläne) */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <SectionHeader 
          icon={FileText}
          title="Beschreibung & Medien"
          expanded={expandedSections.description}
          onToggle={() => toggleSection('description')}
        />
        
        {expandedSections.description && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Kurzbeschreibung * (max. 200 Zeichen)
              </label>
              <textarea
                value={formData.short_description}
                onChange={(e) => setFormData({...formData, short_description: e.target.value})}
                maxLength={200}
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none resize-none"
                placeholder="Kurze Beschreibung deines Angebots..."
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.short_description.length}/200 Zeichen
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Ausführliche Beschreibung *
              </label>
              <textarea
                value={formData.long_description}
                onChange={(e) => setFormData({...formData, long_description: e.target.value})}
                rows={6}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:border-[#2B2142] focus:outline-none resize-none ${!formData.long_description ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
                placeholder="Detaillierte Beschreibung deiner Dienstleistung..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Basis-Preis (ab) *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    value={formData.base_price_numeric || ''}
                    onChange={(e) => {
                      const numValue = e.target.value ? parseInt(e.target.value) : null;
                      setFormData({
                        ...formData,
                        base_price_numeric: numValue,
                        base_price_from: numValue ? `ab ${numValue} €` : ''
                      });
                    }}
                    className={`w-full px-4 py-3 pr-10 border-2 rounded-xl focus:border-[#2B2142] focus:outline-none ${!formData.base_price_from ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
                    placeholder="z.B. 500"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Jahre Erfahrung *
                </label>
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={formData.years_of_experience || ''}
                  onChange={(e) => setFormData({...formData, years_of_experience: e.target.value ? parseInt(e.target.value) : null})}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:border-[#2B2142] focus:outline-none ${!formData.years_of_experience || formData.years_of_experience <= 0 ? 'border-orange-300 bg-orange-50' : 'border-gray-300'}`}
                  placeholder="z.B. 5"
                />
              </div>
            </div>

            <div className="p-4 bg-[#FFF0D1]/30 border border-[#2B2142]/20 rounded-xl">
              <p className="text-sm text-[#2B2142]">
                <Info className="w-4 h-4 inline mr-2" />
                Bilder und Videos kannst du im Tab "Medien" hochladen
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Section 3: Angebote & Spezialisierungen (Plus/Pro) */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden relative">
        <SectionHeader 
          icon={Package}
          title="Angebote & Spezialisierungen"
          expanded={expandedSections.offers}
          onToggle={() => toggleSection('offers')}
          locked={!canAccessPlus()}
          plan="Plus"
        />
        
        {expandedSections.offers && (
          <div className="p-6 space-y-6 relative">
            {!canAccessPlus() && <LockedOverlay plan="Plus" />}
            
            <div className={!canAccessPlus() ? 'blur-sm pointer-events-none' : ''}>
              {/* Specializations */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Spezialisierungen
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    id="spec-input"
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                    placeholder="z.B. Hochzeiten, Firmenfeiern..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addToArray('specializations', e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('spec-input');
                      addToArray('specializations', input.value);
                      input.value = '';
                    }}
                    className="px-4 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white rounded-xl transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.specializations.map((spec, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFF0D1] text-[#2B2142] rounded-full text-sm"
                    >
                      {spec}
                      <button
                        onClick={() => removeFromArray('specializations', index)}
                        className="hover:text-[#2B2142]"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Packages */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-semibold text-gray-700">Pakete</label>
                  <button
                    onClick={addPackage}
                    className="px-4 py-2 bg-[#2B2142] hover:bg-[#1E1730] text-white rounded-lg text-sm flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Paket hinzufügen
                  </button>
                </div>
                
                <div className="space-y-4">
                  {formData.packages.map((pkg, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-xl">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-semibold text-gray-900">Paket {index + 1}</h4>
                        <button
                          onClick={() => removePackage(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={pkg.name}
                          onChange={(e) => updatePackage(index, 'name', e.target.value)}
                          placeholder="Paketname"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <textarea
                          value={pkg.description}
                          onChange={(e) => updatePackage(index, 'description', e.target.value)}
                          placeholder="Beschreibung"
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                        />
                        <input
                          type="text"
                          value={pkg.price_from}
                          onChange={(e) => updatePackage(index, 'price_from', e.target.value)}
                          placeholder="Preis (z.B. ab 1500 €)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Sprachen
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    id="lang-input"
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                    placeholder="z.B. Deutsch, Englisch..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addToArray('languages', e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('lang-input');
                      addToArray('languages', input.value);
                      input.value = '';
                    }}
                    className="px-4 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white rounded-xl transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.languages.map((lang, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm"
                    >
                      {lang}
                      <button
                        onClick={() => removeFromArray('languages', index)}
                        className="hover:text-green-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Verfügbarkeits-Hinweis
                </label>
                <input
                  type="text"
                  value={formData.availability_hint}
                  onChange={(e) => setFormData({...formData, availability_hint: e.target.value})}
                  maxLength={60}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  placeholder="z.B. hauptsächlich am Wochenende"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.availability_hint.length}/60 Zeichen
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section 4: Vertrauen & Referenzen (Plus/Pro) */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden relative">
        <SectionHeader 
          icon={Award}
          title="Vertrauen & Referenzen"
          expanded={expandedSections.trust}
          onToggle={() => toggleSection('trust')}
          locked={!canAccessPlus()}
          plan="Plus"
        />
        
        {expandedSections.trust && (
          <div className="p-6 space-y-6 relative">
            {!canAccessPlus() && <LockedOverlay plan="Plus" />}
            
            <div className={!canAccessPlus() ? 'blur-sm pointer-events-none' : ''}>
              {/* Top References - Linked Providers */}
              <ProviderReferences />

              {/* Certifications */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Zertifikate
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    id="cert-input"
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                    placeholder="z.B. ISO 9001, IHK Zertifikat..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addToArray('certifications', e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('cert-input');
                      addToArray('certifications', input.value);
                      input.value = '';
                    }}
                    className="px-4 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white rounded-xl transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.certifications.map((cert, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm"
                    >
                      {cert}
                      <button
                        onClick={() => removeFromArray('certifications', index)}
                        className="hover:text-purple-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section 5: B2B & Profi-Infos (Pro only) */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden relative">
        <SectionHeader 
          icon={Briefcase}
          title="B2B & Profi-Infos"
          expanded={expandedSections.b2b}
          onToggle={() => toggleSection('b2b')}
          locked={!canAccessPro()}
          plan="Pro"
        />
        
        {expandedSections.b2b && (
          <div className="p-6 space-y-6 relative">
            {!canAccessPro() && <LockedOverlay plan="Pro" />}
            
            <div className={!canAccessPro() ? 'blur-sm pointer-events-none' : ''}>
              {/* B2B Focus */}
              <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.b2b_focus}
                  onChange={(e) => setFormData({...formData, b2b_focus: e.target.checked})}
                  className="w-5 h-5 text-[#2B2142] rounded"
                />
                <div>
                  <div className="font-semibold text-gray-900">B2B-Dienstleistungen</div>
                  <div className="text-sm text-gray-600">Ich biete Dienstleistungen für Firmen an</div>
                </div>
              </label>

              {formData.b2b_focus && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      B2B Event-Typen
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        id="b2b-input"
                        className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                        placeholder="z.B. Konferenzen, Messen, Incentives..."
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addToArray('b2b_event_types', e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          const input = document.getElementById('b2b-input');
                          addToArray('b2b_event_types', input.value);
                          input.value = '';
                        }}
                        className="px-4 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white rounded-xl transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.b2b_event_types.map((type, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-100 text-indigo-800 rounded-full text-sm"
                        >
                          {type}
                          <button
                            onClick={() => removeFromArray('b2b_event_types', index)}
                            className="hover:text-indigo-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      B2B Beschreibung
                    </label>
                    <textarea
                      value={formData.b2b_description}
                      onChange={(e) => setFormData({...formData, b2b_description: e.target.value})}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none resize-none"
                      placeholder="Spezielle Informationen für Firmenkunden..."
                    />
                  </div>
                </>
              )}

              {/* Company Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Kapazität / Eventgröße
                  </label>
                  <select
                    value={formData.capacity_range}
                    onChange={(e) => setFormData({...formData, capacity_range: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="bis 100">bis 100</option>
                    <option value="100-500">100-500</option>
                    <option value="500+">500+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Reisebereitschaft
                  </label>
                  <select
                    value={formData.travel_scope}
                    onChange={(e) => setFormData({...formData, travel_scope: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="regional">Regional</option>
                    <option value="deutschlandweit">Deutschlandweit</option>
                    <option value="DACH/Europa">DACH/Europa</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Rechtsform
                  </label>
                  <input
                    type="text"
                    value={formData.company_legal_form}
                    onChange={(e) => setFormData({...formData, company_legal_form: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                    placeholder="z.B. GmbH, Einzelunternehmen"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Teamgröße
                  </label>
                  <select
                    value={formData.team_size_range}
                    onChange={(e) => setFormData({...formData, team_size_range: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="1-3">1-3</option>
                    <option value="4-10">4-10</option>
                    <option value="10+">10+</option>
                  </select>
                </div>
              </div>

              {/* Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.has_liability_insurance}
                    onChange={(e) => setFormData({...formData, has_liability_insurance: e.target.checked})}
                    className="w-5 h-5 text-[#2B2142] rounded"
                  />
                  <span className="font-semibold text-gray-900">Haftpflichtversicherung vorhanden</span>
                </label>

                <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.has_vat_id}
                    onChange={(e) => setFormData({...formData, has_vat_id: e.target.checked})}
                    className="w-5 h-5 text-[#2B2142] rounded"
                  />
                  <span className="font-semibold text-gray-900">USt-ID vorhanden</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  B2B Rechnungs-Hinweis
                </label>
                <input
                  type="text"
                  value={formData.b2b_invoice_hint}
                  onChange={(e) => setFormData({...formData, b2b_invoice_hint: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                  placeholder="z.B. B2B-konforme Rechnungen, USt-ID vorhanden"
                />
              </div>

              {/* Reference Clients */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Referenzkunden (max. 5 Firmen, je max. 30 Zeichen)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    id="ref-client-input"
                    maxLength={30}
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-[#2B2142] focus:outline-none"
                    placeholder="Firmenname (max. 30 Zeichen)..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && formData.reference_clients.length < 5) {
                        e.preventDefault();
                        addToArray('reference_clients', e.target.value.slice(0, 30));
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      if (formData.reference_clients.length < 5) {
                        const input = document.getElementById('ref-client-input');
                        addToArray('reference_clients', input.value.slice(0, 30));
                        input.value = '';
                      } else {
                        toast.error('Maximal 5 Referenzkunden');
                      }
                    }}
                    disabled={formData.reference_clients.length >= 5}
                    className="px-4 py-3 bg-[#2B2142] hover:bg-[#1E1730] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.reference_clients.map((client, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm"
                    >
                      {client}
                      <button
                        onClick={() => removeFromArray('reference_clients', index)}
                        className="hover:text-amber-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="sticky bottom-0 bg-white border-t-2 border-gray-200 p-4 rounded-t-2xl shadow-lg">
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full px-6 py-4 bg-gradient-to-r from-[#2B2142] to-[#3D3158] hover:from-[#1E1730] hover:to-[#2B2142] text-white font-bold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-3 text-lg"
        >
          <Save className="w-6 h-6" />
          {saving ? 'Speichere...' : 'Änderungen speichern'}
        </button>
      </div>
    </div>
  );
};

export default ProviderProfileEdit;
